import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Toggle } from "../toggle.tsx";
import Separator from "../separator";
import { formatPriceNOK } from "../../../utilities/conversions.js";
import { SaveIcon, SendIcon } from "lucide-react";
import Swal from "sweetalert2";
import useEscapeKey from "../../../hooks/useEscapeKey.tsx";

export function SendInvoiceModal({
    show,
    onClose,
    selectedInvoice,
    baseUrl,
    currentUser,
    client,
}) {

    const [sendEmail, setSendEmail] = useState(true);
    const [ccEmail, setCcEmail] = useState("");
    const [bccEmail, setBccEmail] = useState("");
    const [stripeLink, setStripeLink] = useState("");
    const [message, setMessage] = useState("");
    const [messageEdited, setMessageEdited] = useState(false);
    const [showSignature, setShowSignature] = useState(true);

    const [loading, setLoading] = useState(false);

    useEscapeKey(true, onClose);

    // const saveDraft = () => {
    //     console.log("Save draft", { ccEmail, bccEmail, stripeLink, message });
    // }

    const sendInvoice = () => {
        console.log("Send invoice", { ccEmail, bccEmail, stripeLink, message });
        //call API to send email with invoice PDF link and message
        var formData = new FormData();
        formData.append("invoice", JSON.stringify(selectedInvoice));
        formData.append("token", currentUser.token);
        formData.append("client_uid", client.uid);
        formData.append("ccEmail", ccEmail);
        formData.append("bccEmail", bccEmail);
        formData.append("message", message);
        formData.append("signature", showSignature ? `<p style="color: #7B2FBE; font-weight: bold">${selectedInvoice.agent}</p><br>Chamcore Technologies · Fakturering<br>📞 +${selectedInvoice.agentPhone} | ✉ ${selectedInvoice.agentEmail}<br>${client.client_name}` : ``);

        try {
            setLoading(true);
            const response = axios.post(`${baseUrl}/agentSendInvoiceToCustomer`, formData);
            if (response.data.status === "OK") {
                Swal.fire({
                    icon: "success",
                    title: "Faktura sendt",
                    text: `Fakturaen til ${selectedInvoice.customer} har blitt sendt på e-post.`,
                    timer: 3000,
                    showConfirmButton: false,
                });
            }
        } catch (err) {
            console.error("Error sending invoice:", err);
        } finally {
            setLoading(false);
            onClose();
            //refresh cc bcc message fields to empty
            setCcEmail("");
            setBccEmail("");
            setStripeLink("");
            setMessage("");
            setMessageEdited(false);
        }
    }

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        className="relative bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                    >


                        <div className="flex items-start justify-between gap-3 bg-gradient-to-br from-[#7B2FBE] to-[#9B59D0] px-5 py-[22px] sm:px-7">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 text-lg font-bold text-white"><SendIcon size={18} /> Send Invoice</div>
                                <div className="mt-0.5 text-[13px] text-white/75">{selectedInvoice.customer}</div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                <button onClick={onClose} className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-white/15 text-lg text-white transition hover:bg-white/30" aria-label="Lukk">✕</button>
                            </div>
                        </div>

                        {loading && (
                            <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/75">
                                <div className="loader" />Wait sending invoice...
                            </div>
                        )}
                        {!loading && (
                            <div className="flex flex-col gap-5 px-5 py-5 sm:px-7 sm:py-7">
                                <div className="flex items-center justify-between rounded-[10px] border-[1.5px] border-[#D8B4FE] bg-[#F3EAFD] px-4 py-3">
                                    <div>
                                        <div className="text-xs font-semibold uppercase tracking-[0.04em] text-[#6B6579]">faktura</div>
                                        <div className="text-[15px] font-bold text-[#7B2FBE]">{selectedInvoice.id}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs font-semibold uppercase tracking-[0.04em] text-[#6B6579]">belop</div>
                                        <div className="text-[15px] font-bold text-[#7B2FBE]">{formatPriceNOK(selectedInvoice.amount)}</div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-[13px] font-semibold uppercase tracking-[0.04em] text-[#6B6579]">til</label>
                                    <div className="relative flex items-center">
                                        <span className="pointer-events-none absolute left-[13px] text-[15px] text-[#9B59D0]">✉</span>
                                        <input value={selectedInvoice.email} type="email" placeholder={"kunde@eksempel.no"} className={`w-full rounded-[10px] border-[1.5px] bg-[#FAFAFA] py-[11px] pl-[38px] pr-3.5 text-sm outline-none transition focus:border-[#7B2FBE] focus:bg-white focus:shadow-[0_0_0_3px_rgba(123,47,190,0.12)]`} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[13px] font-semibold uppercase tracking-[0.04em] text-[#6B6579]">CC</label>
                                        <div className="relative flex items-center"><span className="pointer-events-none absolute left-[13px] text-[15px] text-[#9B59D0]">＋</span><input value={ccEmail} onChange={(e) => setCcEmail(e.target.value)} type="email" placeholder={"cc@eksempel.no"} className="w-full rounded-[10px] border-[1.5px] border-[#E4E0EC] bg-[#FAFAFA] py-[11px] pl-[38px] pr-3.5 text-sm outline-none transition focus:border-[#7B2FBE] focus:bg-white focus:shadow-[0_0_0_3px_rgba(123,47,190,0.12)]" /></div>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[13px] font-semibold uppercase tracking-[0.04em] text-[#6B6579]">BCC</label>
                                        <div className="relative flex items-center"><span className="pointer-events-none absolute left-[13px] text-[15px] text-[#9B59D0]">◎</span><input value={bccEmail} onChange={(e) => setBccEmail(e.target.value)} type="email" placeholder={"bcc@eksempel.no"} className="w-full rounded-[10px] border-[1.5px] border-[#E4E0EC] bg-[#FAFAFA] py-[11px] pl-[38px] pr-3.5 text-sm outline-none transition focus:border-[#7B2FBE] focus:bg-white focus:shadow-[0_0_0_3px_rgba(123,47,190,0.12)]" /></div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-[13px] font-semibold uppercase tracking-[0.04em] text-[#6B6579]">Stripe faktura- / kvitteringslenke</label>
                                    <div className="relative flex items-center"><span className="pointer-events-none absolute left-[13px] text-[15px] text-[#9B59D0]">🔗</span><input value={selectedInvoice.link} onChange={(e) => setStripeLink(e.target.value)} type="url" placeholder="https://invoice.stripe.com/i/..." className="w-full rounded-[10px] border-[1.5px] border-[#E4E0EC] bg-[#FAFAFA] py-[11px] pl-[38px] pr-3.5 text-sm outline-none transition focus:border-[#7B2FBE] focus:bg-white focus:shadow-[0_0_0_3px_rgba(123,47,190,0.12)]" /></div>
                                    <div className="mt-0.5 text-xs text-[#6B6579]">Lim inn betalingslenken fra Stripe-dashbordet ditt.</div>
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-[13px] font-semibold uppercase tracking-[0.04em] text-[#6B6579]">Melding</label>
                                    <textarea value={message} onChange={(e) => { setMessage(e.target.value); setMessageEdited(true); }} className="min-h-[100px] w-full resize-y rounded-[10px] border-[1.5px] border-[#E4E0EC] bg-[#FAFAFA] px-3.5 py-[11px] text-sm leading-relaxed outline-none transition focus:border-[#7B2FBE] focus:bg-white focus:shadow-[0_0_0_3px_rgba(123,47,190,0.12)]" />
                                </div>

                                <div className="rounded-[10px] border-[1.5px] border-l-4 border-[#E4E0EC] border-l-[#7B2FBE] bg-[#F8F7FA] px-4 py-3.5">
                                    <div className="flex cursor-pointer select-none items-center justify-between" onClick={() => setShowSignature((v) => !v)}>
                                        <span className="text-[13px] font-semibold uppercase tracking-[0.04em] text-[#6B6579]">📝 signatur</span>
                                        <button onClick={(e) => { e.stopPropagation(); setShowSignature((v) => !v); }} className={`relative h-[22px] w-10 rounded-full transition ${showSignature ? "bg-[#7B2FBE]" : "bg-[#E4E0EC]"}`}>
                                            <span className={`absolute left-[3px] top-[3px] h-4 w-4 rounded-full bg-white shadow transition ${showSignature ? "translate-x-[18px]" : "translate-x-0"}`} />
                                        </button>
                                    </div>
                                    {showSignature && (
                                        <div className="mt-3.5 border-t border-[#E4E0EC] pt-3.5 text-sm leading-relaxed text-[#1A1523]">
                                            <strong className="text-[15px] text-[#7B2FBE]">{selectedInvoice.agent}</strong><br />
                                            <span className="text-[13px] text-[#6B6579]">Chamcore Technologies · Fakturering</span><br />
                                            <span className="text-[13px] text-[#6B6579]">📞 +{selectedInvoice.agentPhone} &nbsp;|&nbsp; ✉ {selectedInvoice.agentEmail}</span><br />
                                            <span className="mt-2.5 inline-block uppercase rounded-md bg-[#7B2FBE] px-3 py-1 text-xs font-bold tracking-[0.05em] text-white">{client.client_name}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                        )}

                        <div className="flex flex-col-reverse gap-3 border-t border-[#E4E0EC] bg-[#F8F7FA] px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-7">
                            <button onClick={onClose} className="inline-flex items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] border-[#FECACA] bg-white px-[18px] py-[9px] text-[13px] font-semibold text-[#EF4444] transition hover:bg-[#FEE2E2] active:scale-[0.97]">Avbryt</button>
                            <div className="flex flex-col gap-2 sm:flex-row">
                                {/* <button onClick={saveDraft} className="inline-flex items-center justify-center gap-1.5 rounded-[10px] border-[1.5px] border-[#E4E0EC] bg-[#F8F7FA] px-[18px] py-[9px] text-[13px] font-semibold text-[#6B6579] transition hover:bg-[#ECECEC] active:scale-[0.97]"><SaveIcon /> Lagre utkast</button> */}
                                <button onClick={sendInvoice} className="inline-flex items-center justify-center gap-1.5 rounded-[10px] bg-[#7B2FBE] px-[18px] py-[9px] text-[13px] font-semibold text-white transition hover:bg-[#5E1F96] active:scale-[0.97]"><SendIcon size={13} /> Send faktura</button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
